import { lazyGettext } from '@eb/lazy-gettext';
import omitBy from 'lodash/omitBy';
import PropTypes from 'prop-types';
import { datePropType } from '@eb/eds-utils';
import DayPickerRangeController from 'react-dates/lib/components/DayPickerRangeController';

export const ORANGE_COLOR = 'orange';
export const BLUE_COLOR = 'blue';

export const COLOR_PROP = [ORANGE_COLOR, BLUE_COLOR];
export const COLOR_PROP_TYPE = PropTypes.oneOf([ORANGE_COLOR, BLUE_COLOR]);

export const START_DATE = 'startDate';
export const END_DATE = 'endDate';

export const INITIAL_FOCUS = [START_DATE, END_DATE];

export const RANGE_VALUE = {
    startDate: datePropType,
    endDate: datePropType,
};
export const RANGE_VALUE_PROPTYPE = PropTypes.shape(RANGE_VALUE);

export const DAY_PICKER_RANGE_CONTROLLER_PROP_TYPES = omitBy(
    DayPickerRangeController.propTypes,
    // Remove trap prop set by `prop-types-exact/forbidExtraProps`
    // since we have more props specific to our components.
    (_, key) => key.startsWith('prop-types-exact'),
);

// i18n Phrases
// Based on https://github.com/airbnb/react-dates/blob/master/src/defaultPhrases.js
export const localizedPhrases = {
    calendarLabel: lazyGettext('Calendar'),
    closeDatePicker: lazyGettext('Close'),
    focusStartDate: lazyGettext(
        'Interact with the calendar and add the check-in date for your trip.',
    ),
    clearDate: lazyGettext('Clear Date'),
    clearDates: lazyGettext('Clear Dates'),
    jumpToPrevMonth: lazyGettext(
        'Move backward to switch to the previous month.',
    ),
    jumpToNextMonth: lazyGettext('Move forward to switch to the next month.'),
    keyboardShortcuts: lazyGettext('Keyboard Shortcuts'),
    showKeyboardShortcutsPanel: lazyGettext(
        'Open the keyboard shortcuts panel.',
    ),
    hideKeyboardShortcutsPanel: lazyGettext('Close the shortcuts panel.'),
    openThisPanel: lazyGettext('Open this panel.'),
    enterKey: lazyGettext('Enter key'),
    leftArrowRightArrow: lazyGettext('Right and left arrow keys'),
    upArrowDownArrow: lazyGettext('up and down arrow keys'),
    pageUpPageDown: lazyGettext('page up and page down keys'),
    homeEnd: lazyGettext('Home and end keys'),
    escape: lazyGettext('Escape key'),
    questionMark: lazyGettext('Question mark'),
    selectFocusedDate: lazyGettext('Select the date in focus.'),
    moveFocusByOneDay: lazyGettext(
        'Move backward (left) and forward (right) by one day.',
    ),
    moveFocusByOneWeek: lazyGettext(
        'Move backward (up) and forward (down) by one week.',
    ),
    moveFocusByOneMonth: lazyGettext('Switch months.'),
    moveFocustoStartAndEndOfWeek: lazyGettext(
        'Go to the first or last day of a week.',
    ),
    returnFocusToInput: lazyGettext('Return to the date input field.'),
    keyboardNavigationInstructions: lazyGettext(
        'Press the down arrow key to interact with the calendar and select a date. Press the question mark key to get the keyboard shortcuts for changing dates.',
    ),
    chooseAvailableStartDate: ({ date }) =>
        lazyGettext('Choose %(date)s as your check-in date. It’s available.', {
            date,
        }),
    chooseAvailableEndDate: ({ date }) =>
        lazyGettext('Choose %(date)s as your check-out date. It’s available.', {
            date,
        }),
    chooseAvailableDate: ({ date }) =>
        lazyGettext('Select this date. %(date)s', { date }),
    dateIsUnavailable: ({ date }) =>
        lazyGettext('Not available. %(date)s', { date }),
    dateIsSelected: ({ date }) => lazyGettext('Selected. %(date)s', { date }),
    dateIsSelectedAsStartDate: ({ date }) =>
        lazyGettext('Selected as start date. %(date)s', { date }),
    dateIsSelectedAsEndDate: ({ date }) =>
        lazyGettext('Selected as end date. %(date)s', { date }),
};
